import React from 'react';
import { Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { CommTemplate } from './TemplateLibrary';

// ---- Helpers ----

function fmtRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---- Props ----

interface TemplateVersionHistoryProps {
  versions: CommTemplate[];
}

// ---- Component ----

export function TemplateVersionHistory({ versions }: TemplateVersionHistoryProps): React.ReactElement {
  if (versions.length === 0) {
    return (
      <div className="p-4 text-sm text-ink-muted text-center">
        No version history available.
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <p className="px-4 py-2 text-xs font-semibold text-ink-muted uppercase tracking-wider border-b border-sunken">
        Version History
      </p>

      <div className="flex flex-col divide-y divide-sunken">
        {versions.map((v, idx) => {
          const isLatest = idx === 0;
          return (
            <div
              key={v.id}
              className={cn(
                'flex items-start gap-3 px-4 py-3',
                isLatest ? 'bg-brand-tint' : 'bg-surface',
              )}
            >
              {/* Version number */}
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-sunken flex items-center justify-center text-xs font-semibold text-ink-soft">
                v{v.version}
              </div>

              {/* Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-ink">
                    {v.createdBy ?? (v.isSystem ? 'System' : 'Unknown')}
                  </span>
                  {isLatest && (
                    <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-brand text-on-brand">
                      Latest
                    </span>
                  )}
                </div>

                {/* Timestamp */}
                <div className="flex items-center gap-1 mt-0.5 text-xs text-ink-muted">
                  <Clock className="h-3 w-3" aria-hidden />
                  <span>{fmtRelative(v.createdAt)}</span>
                </div>

                {/* Approval status */}
                <div className="mt-1">
                  {v.approvedBy !== null ? (
                    <span className="inline-flex items-center gap-1 text-xs text-pass font-medium">
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                      Approved by {v.approvedBy}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-warn font-medium">
                      <AlertCircle className="h-3.5 w-3.5" aria-hidden />
                      Pending approval
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
